import type { ReactElement } from 'react';

import { useCallback } from 'react';
import { useSortBy } from 'react-instantsearch';

import { OptionGroup } from './OptionGroup';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';
import { parseSortingOptionsDataLayer } from '~/utilities/vendors';

type SortingOptionItem = {
  value: string;
  label: string;
  suffix: string;
};

const SORTING_CONFIG = {
  DEFAULT_EXPANDED: false,
  CHECKBOX_TYPE: 'round' as const,
  TRANSPARENT_BG: 'transparent',
  SKIP_DEFAULT_OPTION: true,
} as const;

type SortingOptionProps = {
  isExpanded?: boolean;
  onToggle?: () => void;
};

export const SortingOption = ({ isExpanded, onToggle }: SortingOptionProps = {}): ReactElement => {
  const { systemTexts } = useProductListContext();
  const { sortingOptions } = useAlgoliaSortingOptions();
  const { refine, currentRefinement, canRefine } = useSortBy({ items: sortingOptions });
  const { pushToDataLayer } = useDataLayerContext();

  const handleSelect = useCallback(
    (key: string) => {
      if (!canRefine) {
        return;
      }

      refine(key);

      const selectedOption = sortingOptions.find(option => option.value === key);

      if (selectedOption) {
        pushToDataLayer(parseSortingOptionsDataLayer(selectedOption.labelEN));
      }
    },
    [canRefine, refine, sortingOptions, pushToDataLayer]
  );

  if (!sortingOptions?.length) {
    return <></>;
  }

  const selected = sortingOptions.find((option, index) =>
    SORTING_CONFIG.SKIP_DEFAULT_OPTION
      ? index > 0 && option.value === currentRefinement
      : option.value === currentRefinement
  );

  return (
    <OptionGroup
      title={systemTexts?.filterAndSort?.sortByLabel || ''}
      summary={selected?.label || ''}
      valueEN="sort"
      items={sortingOptions.map(({ value, label, suffix }: SortingOptionItem) => ({
        key: value,
        label,
        selected: value === currentRefinement,
        bgColor: SORTING_CONFIG.TRANSPARENT_BG,
        testId: `sort-option-${suffix}`,
      }))}
      onSelect={handleSelect}
      defaultExpanded={SORTING_CONFIG.DEFAULT_EXPANDED}
      checkboxType={SORTING_CONFIG.CHECKBOX_TYPE}
      isExpanded={isExpanded}
      onToggle={onToggle}
    />
  );
};
